import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Building2, ArrowRight, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import CitizenAuthLayout from '@shared/ui/CitizenAuthLayout';
import AuthHeader from '@shared/ui/AuthHeader';
import InlineAlert from '@shared/ui/InlineAlert';
import Button from '@shared/ui/Button';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

/**
 * Landing page for an organization sign-up link (/join/:code).
 * - Not signed in -> straight to the citizen signup form (?code=), which names
 *   the organization being joined and explains a dead link itself. Invitees see
 *   the same "Let's get you set up" screen as every new user, plus the
 *   affiliation; its "Sign in" link returns here to confirm instead.
 * - Signed in, invalid/expired code -> explain, offer the app.
 * - Signed in, valid code -> "Join [Org]" confirmation that creates the membership.
 */
const JoinOrganizationPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('token');
  const signupPath = `/citizen/signup?code=${encodeURIComponent(code || '')}`;

  const {
    data: lookup,
    isLoading,
  } = useQuery(
    ['signup-code', code],
    async () => {
      const { data } = await api.get(`/auth/signup-code/${encodeURIComponent(code || '')}`);
      return data?.data;
    },
    { enabled: !!code && isLoggedIn, retry: false },
  );

  const joinMutation = useMutation(
    async () => {
      const { data } = await api.post('/organizations/join-by-code', { code });
      return data?.data;
    },
  );

  if (!isLoggedIn) {
    return <Navigate to={signupPath} replace />;
  }

  if (isLoading) {
    return (
      <CitizenAuthLayout>
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" label="Checking your invitation…" />
        </div>
      </CitizenAuthLayout>
    );
  }

  const isValid = lookup?.valid;
  const orgName = lookup?.organizationName as string | undefined;

  // --- Invalid / expired / unknown code -------------------------------------
  if (!isValid) {
    return (
      <CitizenAuthLayout>
        <AuthHeader
          title="Invitation link no longer active"
          subtitle="This sign-up link isn't active anymore. You can still create a free Capitol Commons account."
          icon={<Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />}
        />
        <div className="mt-8">
          <Button
            onClick={() => navigate('/citizen/signup')}
            variant="primary"
            size="lg"
            fullWidth
            icon={ArrowRight}
            iconPosition="right"
            noShadow
          >
            Continue signing up
          </Button>
        </div>
      </CitizenAuthLayout>
    );
  }

  // --- Valid code, already signed in: confirm join --------------------------
  if (isLoggedIn) {
    const result = joinMutation.data;
    if (joinMutation.isSuccess) {
      return (
        <CitizenAuthLayout>
          <AuthHeader
            title={result?.alreadyMember ? `You're already a member` : `You've joined ${orgName}`}
            subtitle={result?.alreadyMember
              ? `You already belong to ${orgName}.`
              : `${orgName} has been added to your account.`}
            icon={<CheckCircle className="w-8 h-8 text-primary-600 dark:text-primary-400" />}
          />
          <div className="mt-8">
            <Button
              onClick={() => { window.location.href = '/citizen/home'; }}
              variant="primary"
              size="lg"
              fullWidth
              icon={ArrowRight}
              iconPosition="right"
              noShadow
            >
              Go to Capitol Commons
            </Button>
          </div>
        </CitizenAuthLayout>
      );
    }

    return (
      <CitizenAuthLayout>
        <AuthHeader
          title={`Join ${orgName}`}
          subtitle={`Confirm to add ${orgName} to your Capitol Commons account.`}
          icon={<Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />}
        />
        <div className="mt-8 space-y-4">
          {joinMutation.isError && (
            <InlineAlert
              message={(joinMutation.error as any)?.response?.data?.error || 'Something went wrong. Please try again.'}
              variant="error"
            />
          )}
          <Button
            onClick={() => joinMutation.mutate()}
            variant="primary"
            size="lg"
            fullWidth
            loading={joinMutation.isLoading}
            icon={ArrowRight}
            iconPosition="right"
            noShadow
          >
            {joinMutation.isLoading ? 'Joining…' : `Join ${orgName}`}
          </Button>
        </div>
      </CitizenAuthLayout>
    );
  }
};

export default JoinOrganizationPage;
